/**
 * Black Friday Page Tracking
 * Track tier selections, bundle interactions, and upsells
 */

class BlackFridayTracker {
  constructor() {
    this.timerStarted = false;
    this.bundleViewed = false;
    this.tierInteractions = [];
    this.cartValue = 0;
    
    this.init();
  }

  init() {
    this.setupTierTracking();
    this.setupBundleTracking();
    this.setupTimerTracking();
    this.setupCartTracking();
    this.setupScrollTracking();
  }

  setupTierTracking() {
    // Track tier card clicks (Bronze, Silver, Gold)
    document.addEventListener('click', (e) => {
      const tierCard = e.target.closest('[class*="tier_card"], .tier-card');
      if (tierCard) {
        this.trackTierSelection(tierCard);
      }
    });

    // Track product interactions within tiers
    document.addEventListener('click', (e) => {
      const productElement = e.target.closest('[class*="product"], .product-item');
      if (productElement) {
        this.trackProductInteraction(productElement);
      }
    });
  }

  trackTierSelection(tierElement) {
    const tierType = this.extractTierType(tierElement);
    const tierData = this.getTierData(tierElement);
    
    // Prevent duplicate tracking
    if (this.tierInteractions.includes(tierType)) return;
    this.tierInteractions.push(tierType);

    if (window.fbq) {
      fbq('trackCustom', 'TierSelection', {
        content_name: `${tierType} Tier Selected`,
        content_category: 'Product Interest',
        tier_type: tierType,
        price_range: tierData.priceRange,
        interaction_type: 'tier_selection'
      });
    }

    if (window.leadTracker) {
      window.leadTracker.trackEvent('TierSelection', {
        content_name: `${tierType} Tier Selected`,
        content_category: 'Product Interest',
        tier_type: tierType,
        price_range: tierData.priceRange,
        entries_offered: tierData.entries
      });
    }
  }

  extractTierType(element) {
    const classList = element.className.toLowerCase();
    const textContent = element.textContent.toLowerCase();
    
    if (classList.includes('bronze') || textContent.includes('bronze')) return 'bronze';
    if (classList.includes('silver') || textContent.includes('silver')) return 'silver';
    if (classList.includes('gold') || textContent.includes('gold')) return 'gold';
    
    // Check for "baller pup bundle" which is gold tier
    if (textContent.includes('baller') || textContent.includes('bundle')) return 'gold';
    
    return 'unknown';
  }

  getTierData(element) {
    const text = element.textContent;
    let priceRange = 'unknown';
    let entries = 0;

    // Extract price information
    if (text.includes('From $10')) priceRange = 'entry_level';
    if (text.includes('From $79')) priceRange = 'mid_tier';
    if (text.includes('$499')) priceRange = 'premium';

    // Extract entries information
    const entriesMatch = text.match(/(\d+)\s+(?:bonus\s+)?entri/i);
    if (entriesMatch) {
      entries = parseInt(entriesMatch[1]);
    }

    return { priceRange, entries };
  }

  trackProductInteraction(productElement) {
    const productData = this.extractProductData(productElement);

    if (window.fbq) {
      fbq('trackCustom', 'ProductInteraction', {
        content_name: productData.name || 'Product Interaction',
        content_category: 'Product Interest',
        value: productData.price || 0,
        currency: 'USD',
        entries_count: productData.entries || 0
      });
    }

    if (window.leadTracker) {
      window.leadTracker.trackEvent('ProductInteraction', {
        content_name: productData.name || 'Product Clicked',
        content_category: 'Product Interest',
        value: productData.price || 0,
        currency: 'USD',
        entries_count: productData.entries || 0,
        product_type: productData.type || 'unknown'
      });
    }
  }

  extractProductData(element) {
    const text = element.textContent;
    const data = {
      name: null,
      price: 0,
      entries: 0,
      type: 'product'
    };

    // Extract entries count
    const entriesMatch = text.match(/(\d+)\s+entri/i);
    if (entriesMatch) {
      data.entries = parseInt(entriesMatch[1]);
    }

    // Extract price
    const priceMatch = text.match(/\$(\d+(?:,\d{3})*(?:\.\d{2})?)/);
    if (priceMatch) {
      data.price = parseFloat(priceMatch[1].replace(',', ''));
    }

    // Extract product name (simplified)
    const productNameElement = element.querySelector('[class*="title"], [class*="name"], h3, h4');
    if (productNameElement) {
      data.name = productNameElement.textContent.trim();
    }

    return data;
  }

  setupBundleTracking() {
    // Track bundle card interactions
    document.addEventListener('click', (e) => {
      const bundleElement = e.target.closest('.dev-friday-ties-second-bundle-card, [class*="bundle"]');
      if (bundleElement) {
        this.trackBundleInteraction(bundleElement);
      }
    });

    // Track bundle visibility
    const bundleCards = document.querySelectorAll('.dev-friday-ties-second-bundle-card');
    bundleCards.forEach(card => {
      this.observeBundleVisibility(card);
    });
  }

  trackBundleInteraction(bundleElement) {
    const bundleData = this.extractBundleData(bundleElement);

    if (window.fbq) {
      fbq('trackCustom', 'Upsell', {
        content_name: 'Baller Pup Bundle Interest',
        content_category: 'High Value Product',
        value: bundleData.price || 499,
        currency: 'USD',
        product_type: 'bundle',
        entries_bonus: bundleData.entries || 49,
        interaction_type: 'bundle_click'
      });
    }

    if (window.leadTracker) {
      window.leadTracker.trackUpsell('baller_pup_bundle', bundleData.price || 499, {
        entries_bonus: bundleData.entries || 49,
        savings_amount: bundleData.savings || 609,
        interaction_type: 'bundle_click'
      });
    }
  }

  observeBundleVisibility(bundleElement) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.bundleViewed) {
          this.bundleViewed = true;
          this.trackBundleView();
          observer.disconnect();
        }
      });
    }, { threshold: 0.5 });

    observer.observe(bundleElement);
  }

  trackBundleView() {
    if (window.fbq) {
      fbq('trackCustom', 'BundleView', {
        content_name: 'Baller Pup Bundle Viewed',
        content_category: 'High Value Product',
        value: 499,
        currency: 'USD',
        interaction_type: 'bundle_view'
      });
    }

    if (window.leadTracker) {
      window.leadTracker.trackEvent('BundleView', {
        content_name: 'High Value Bundle in View',
        content_category: 'Product Interest',
        value: 499,
        currency: 'USD',
        interaction_type: 'bundle_view'
      });
    }
  }

  extractBundleData(element) {
    const text = element.textContent;
    const data = {
      price: 499,
      entries: 49,
      savings: 609
    };

    // Extract price
    const priceMatch = text.match(/\$(\d+)/);
    if (priceMatch) {
      data.price = parseInt(priceMatch[1]);
    }

    // Extract entries
    const entriesMatch = text.match(/(\d+)\s+(?:bonus\s+)?entri/i);
    if (entriesMatch) {
      data.entries = parseInt(entriesMatch[1]);
    }

    // Extract savings
    const savingsMatch = text.match(/save\s+\$(\d+)/i);
    if (savingsMatch) {
      data.savings = parseInt(savingsMatch[1]);
    }

    return data;
  }

  setupTimerTracking() {
    // Track when timer becomes visible
    const timerElements = document.querySelectorAll('[data-dev-friday-timer]');
    timerElements.forEach(timer => {
      this.observeTimerVisibility(timer);
    });

    // Hook into existing timer if available
    if (window.devFridayTimer) {
      this.enhanceExistingTimer();
    } else {
      // Wait for timer to initialize
      setTimeout(() => {
        if (window.devFridayTimer) {
          this.enhanceExistingTimer();
        }
      }, 2000);
    }
  }

  observeTimerVisibility(timerElement) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.timerStarted) {
          this.timerStarted = true;
          this.trackTimerView();
          observer.disconnect();
        }
      });
    });

    observer.observe(timerElement);
  }

  trackTimerView() {
    if (window.fbq) {
      fbq('trackCustom', 'TimerView', {
        content_name: '10 Minute Timer Viewed',
        content_category: 'Urgency',
        timer_duration: 600
      });
    }

    if (window.leadTracker) {
      window.leadTracker.trackEvent('TimerView', {
        content_name: 'Countdown Timer Viewed',
        content_category: 'Urgency',
        timer_duration: 600
      });
    }
  }

  enhanceExistingTimer() {
    const timer = window.devFridayTimer;
    if (!timer) return;

    // Hook into timer expiry
    const originalExpired = timer.onTimerExpired;
    timer.onTimerExpired = function() {
      // Call original function
      if (originalExpired) {
        originalExpired.call(this);
      }

      // Track expiry event
      window.blackFridayTracker.trackTimerExpiry();
    };

    // Track milestones
    this.trackTimerMilestones(timer);
  }

  trackTimerMilestones(timer) {
    const checkMilestones = () => {
      if (!timer.getRemainingTime) return;
      
      const remaining = timer.getRemainingTime();
      const remainingMinutes = Math.floor(remaining / (1000 * 60));

      // Track when 5 minutes remain
      if (remainingMinutes === 5) {
        this.trackTimerMilestone('5_minutes');
      }

      // Track when 2 minutes remain
      if (remainingMinutes === 2) {
        this.trackTimerMilestone('2_minutes');
      }

      // Track when 1 minute remains
      if (remainingMinutes === 1) {
        this.trackTimerMilestone('1_minute');
      }
    };

    // Check milestones every 30 seconds
    setInterval(checkMilestones, 30000);
  }

  trackTimerMilestone(milestone) {
    if (window.fbq) {
      fbq('trackCustom', 'TimerMilestone', {
        content_name: `Timer Milestone - ${milestone}`,
        content_category: 'Urgency',
        milestone: milestone
      });
    }

    if (window.leadTracker) {
      window.leadTracker.trackEvent('TimerMilestone', {
        content_name: `Timer Milestone - ${milestone}`,
        content_category: 'Urgency',
        milestone: milestone
      });
    }
  }

  trackTimerExpiry() {
    if (window.fbq) {
      fbq('trackCustom', 'TimerExpiry', {
        content_name: 'Deal Timer Expired',
        content_category: 'Urgency',
        timer_duration: 600
      });
    }

    if (window.leadTracker) {
      window.leadTracker.trackEvent('TimerExpiry', {
        content_name: 'Deal Timer Expired',
        content_category: 'Urgency',
        timer_duration: 600,
        page_location: 'black_friday'
      });
    }
  }

  setupCartTracking() {
    // Listen for cart events
    document.addEventListener('theme:cart:add', (e) => {
      this.trackCartAdd(e.detail);
    });

    document.addEventListener('theme:cart:open', () => {
      this.trackCartOpen();
    });

    // Track add to cart button clicks
    document.addEventListener('click', (e) => {
      const addToCartBtn = e.target.closest('[name="add"], .btn--add-to-cart, [class*="add-to-cart"]');
      if (addToCartBtn) {
        this.trackAddToCartIntent(addToCartBtn);
      }
    });
  }

  trackCartAdd(detail) {
    const value = detail.value || detail.price || 0;
    this.cartValue += value;

    if (window.fbq) {
      fbq('trackCustom', 'Upsell', {
        content_name: 'Black Friday Product Added',
        content_category: 'Purchase Intent',
        value: value,
        currency: 'USD',
        total_cart_value: this.cartValue,
        interaction_type: 'add_to_cart'
      });

      // Also fire standard Facebook event
      fbq('track', 'AddToCart', {
        value: value,
        currency: 'USD'
      });
    }

    if (window.leadTracker) {
      window.leadTracker.trackUpsell('black_friday_product', value, {
        total_cart_value: this.cartValue,
        page_source: 'black_friday'
      });
    }
  }

  trackCartOpen() {
    if (window.fbq) {
      fbq('trackCustom', 'CartEngagement', {
        content_name: 'Cart Opened from Black Friday',
        content_category: 'Purchase Intent',
        cart_value: this.cartValue,
        interaction_type: 'cart_open'
      });
    }

    if (window.leadTracker) {
      window.leadTracker.trackEvent('CartEngagement', {
        content_name: 'Cart Drawer Opened',
        content_category: 'Purchase Intent',
        cart_value: this.cartValue,
        source_page: 'black_friday'
      });
    }
  }

  trackAddToCartIntent(button) {
    const productData = this.extractProductDataFromButton(button);

    if (window.fbq) {
      fbq('trackCustom', 'AddToCartIntent', {
        content_name: 'Add to Cart Button Clicked',
        content_category: 'Purchase Intent',
        value: productData.price || 0,
        currency: 'USD',
        product_name: productData.name || 'Unknown Product'
      });
    }

    if (window.leadTracker) {
      window.leadTracker.trackEvent('AddToCartIntent', {
        content_name: 'Add to Cart Intent',
        content_category: 'Purchase Intent',
        value: productData.price || 0,
        currency: 'USD',
        product_name: productData.name || 'Unknown Product'
      });
    }
  }

  extractProductDataFromButton(button) {
    const form = button.closest('form');
    const productContainer = button.closest('[class*="product"]');
    
    let name = 'Unknown Product';
    let price = 0;

    if (productContainer) {
      const titleElement = productContainer.querySelector('[class*="title"], h3, h4');
      if (titleElement) {
        name = titleElement.textContent.trim();
      }

      const priceElement = productContainer.querySelector('[class*="price"]');
      if (priceElement) {
        const priceMatch = priceElement.textContent.match(/\$(\d+(?:,\d{3})*(?:\.\d{2})?)/);
        if (priceMatch) {
          price = parseFloat(priceMatch[1].replace(',', ''));
        }
      }
    }

    return { name, price };
  }

  setupScrollTracking() {
    let scrollDepth = 0;
    const milestones = [25, 50, 75, 100];
    const trackedMilestones = [];

    window.addEventListener('scroll', () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight - windowHeight;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      
      scrollDepth = Math.round((scrollTop / documentHeight) * 100);

      milestones.forEach(milestone => {
        if (scrollDepth >= milestone && !trackedMilestones.includes(milestone)) {
          trackedMilestones.push(milestone);
          this.trackScrollDepth(milestone);
        }
      });
    });
  }

  trackScrollDepth(depth) {
    if (window.fbq) {
      fbq('trackCustom', 'ScrollDepth', {
        content_name: `Scroll Depth ${depth}%`,
        content_category: 'Engagement',
        scroll_depth: depth,
        page_type: 'black_friday'
      });
    }

    if (window.leadTracker) {
      window.leadTracker.trackEvent('ScrollDepth', {
        content_name: `Page Scroll ${depth}%`,
        content_category: 'Engagement',
        scroll_depth: depth,
        page_type: 'black_friday'
      });
    }
  }

  // Public method to track successful purchases
  trackPurchaseConfirmation(orderValue, orderDetails = {}) {
    if (window.fbq) {
      fbq('trackCustom', 'Confirmation', {
        content_name: 'Black Friday Purchase Confirmed',
        content_category: 'Conversion',
        value: orderValue,
        currency: 'USD',
        conversion_type: 'purchase',
        source_page: 'black_friday'
      });
    }

    if (window.leadTracker) {
      window.leadTracker.trackConfirmation('purchase', orderValue, {
        source_page: 'black_friday',
        ...orderDetails
      });
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  // Only initialize on Black Friday page
  if (window.location.pathname.includes('/pages/black-friday')) {
    window.blackFridayTracker = new BlackFridayTracker();
  }
});

// Expose method for external use
window.trackBlackFridayPurchase = (value, details) => {
  if (window.blackFridayTracker) {
    window.blackFridayTracker.trackPurchaseConfirmation(value, details);
  }
};
